//ManageUser.js

import React, { useState, useEffect } from 'react';
import './ManageUser.css';
import { useApi } from '../ApiContext'; 

// =======================================================
// 1. CONSTANTS AND API CONFIGURATION
// =======================================================

const USER_TYPES = {
    CUSTOMER: 'Customer',
    RESELLER: 'Reseller',
    ADMIN: 'Admin',
};

// =======================================================
// 2. USER ROW COMPONENT (The Table Row)
// =======================================================

const UserRow = ({ user, onTypeChange }) => {
    
    const isAdmin = user.userType === USER_TYPES.ADMIN;

    const handleDropdownChange = (event) => {
        if (isAdmin) return; 
        
        const newType = event.target.value;
        onTypeChange(user.id, newType);
    };

    return (
        <tr className={`user-row`}> 
            <td>{user.id}</td>
            <td>{user.name}</td>
            <td>{user.email}</td>
            <td>
                <select
                    disabled={isAdmin} 
                    value={user.userType}
                    onChange={handleDropdownChange}
                    className={`type-select type-${user.userType.toLowerCase()}`}
                >
                    {isAdmin ? (
                        <option value={USER_TYPES.ADMIN}>Admin (Locked)</option>
                    ) : (
                        <>
                            <option value={USER_TYPES.CUSTOMER}>Customer</option>
                            <option value={USER_TYPES.RESELLER}>Reseller</option>
                        </>
                    )}
                </select>
            </td>
        </tr>
    );
};

// =======================================================
// 3. MAIN MANAGE USER COMPONENT (The Dashboard)
// =======================================================

const ManageUser = () => {
    const [users, setUsers] = useState([]);
    const [statusMessage, setStatusMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    
    // ➡️ USE HOOK to get API instance
    const { api } = useApi();
    
    // --- Data Fetching ---
    useEffect(() => {
        fetchUsers();
    }, [api]);

    const fetchUsers = async () => {
        if (!api) return;
        setIsLoading(true);
        try {
            // ➡️ Use the dynamic API instance
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            console.error("Fetch Error:", error);
            setStatusMessage(`❌ Error fetching users: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // --- Data Update ---
    const handleUserTypeChange = async (userId, newType) => {
        const originalUser = users.find(u => u.id === userId);
        
        // Optimistic UI Update
        setUsers(prevUsers => 
            prevUsers.map(user => 
                user.id === userId ? { ...user, userType: newType } : user
            )
        );

        try {
            // ➡️ Use the dynamic API instance
            await api.put(`/users/${userId}/type`, { newType });

            // Success feedback
            setStatusMessage(`✅ User ${userId} (${originalUser.name}) updated to ${newType}.`);
        } catch (error) {
            console.error("Update Error:", error);
            // API failed: Roll back state
            setUsers(prevUsers => 
                prevUsers.map(user => 
                    user.id === userId ? originalUser : user
                )
            );
            setStatusMessage(`❌ Failed to update user ${userId}: ${error.message}. UI reverted.`);
        }
        
        setTimeout(() => setStatusMessage(''), 3000);
    };

    if (isLoading) {
        return <div className="user-dashboard-container"><h3>Loading Users...</h3></div>;
    }

    return (
        <div className="user-dashboard-container">
            <h1>User Management</h1>
            
            {statusMessage && <div className="global-status">{statusMessage}</div>}

            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>User Type</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <UserRow 
                                key={user.id} 
                                user={user} 
                                onTypeChange={handleUserTypeChange}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ManageUser;